package pl.decks.personal.pages.ievp.sections.why

import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontStyle
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import pl.decks.personal.components.widgets.hasdiagram.HASDiagramStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout

object WhySectionStylesheet {
    object Vars {
        val segmentHeight by StyleVariable<CSSLengthOrPercentageNumericValue>(40.vh)
        val emphasisColor by StyleVariable<Color>()

        object Internal {
            val IntroContainerPadding by StyleVariable(SiteLayout.Space.none)
        }
    }

    object Intro {
        val Container = CssStyle.base {
            Modifier
                .gap(SiteLayout.Space.Small)
                .padding(topBottom = Vars.Internal.IntroContainerPadding.value())
        }
        val Text = FontTypography.sansSerif.extendedByBase {
            Modifier
                .maxWidth(SiteLayout.Breakpoint.sm)
                .fillMaxWidth()
                .textAlign(TextAlign.Justify)
                .fontWeight(FontTypography.Weight.Light)
        }
        val Breakline = Text.extendedByBase {
            Modifier.fontStyle(FontStyle.Italic)
        }
        val Conclusion = Text.extendedByBase {
            Modifier.fontWeight(FontTypography.Weight.Bold)
        }
        val ActionPrompt = Text.extendedByBase {
            Modifier
                .textAlign(TextAlign.Center)
                .fontWeight(FontTypography.Weight.Bold)
                .fontSize(FontTypography.Size.Normal)
                .color(Vars.emphasisColor.value(colorMode.toPalette().color))
        }
        val ActionVisual = CssStyle.base {
            Modifier.fontSize(FontTypography.Size.XLarge).color(Vars.emphasisColor.value(colorMode.toPalette().color))
        }
        val Divider = CssStyle.base {
            Modifier
                .border(0.px)
                .borderTop(
                    SiteLayout.Line.Normal,
                    LineStyle.Solid,
                    Vars.emphasisColor.value(colorMode.toPalette().color)
                )
        }
    }

    object RevealingDiagram {
        val StickyContainer = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .position(Position.Sticky)
                .top(SiteLayout.Space.none)
        }
        val NodeText = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Small)
                .color(Vars.emphasisColor.value(colorMode.toPalette().color))
        }
        val InnerNodeText = NodeText.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Large)
                .fontWeight(FontTypography.Weight.Bold)
        }
        val PredicatePrefix = FontTypography.serif.extendedByBase {
            Modifier
                .fontStyle(FontStyle.Italic)
                .fontSize(FontTypography.Size.Large)
                .fontWeight(FontTypography.Weight.Light)
                .color(Vars.emphasisColor.value(colorMode.toPalette().color))
        }
        val PredicateTitle = FontTypography.serif.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Large)
                .fontWeight(FontTypography.Weight.Medium)
                .color(Vars.emphasisColor.value(colorMode.toPalette().color))
        }
        val PredicateText = FontTypography.serif.extendedByBase {
            Modifier.fillMaxWidth().textAlign(TextAlign.Justify).fontStyle(FontStyle.Italic)
        }
    }

    val WhySection = CssStyle {
        base {
            Modifier.fillMaxWidth()
                .gap(SiteLayout.Space.XLarge)
                .setVariable(
                    HASDiagramStylesheet.Vars.nodeLineColor,
                    Vars.emphasisColor.value(colorMode.toPalette().color),
                )
                .setVariable(HASDiagramStylesheet.Vars.nodeBackgroundColor, colorMode.toSitePalette().surface)
                .setVariable(HASDiagramStylesheet.Vars.nodeSize, 70.px)
                .setVariable(HASDiagramStylesheet.Vars.innerNodeSize, 70.px)
        }

        Breakpoint.MD {
            Modifier
                .setVariable(Vars.Internal.IntroContainerPadding, SiteLayout.Space.XXXLarge)
                .setVariable(HASDiagramStylesheet.Vars.nodeSize, 80.px)
                .setVariable(HASDiagramStylesheet.Vars.innerNodeSize, 100.px)
        }
    }
    val SectionFront = CssStyle {
        base {
            Modifier.fillMaxWidth().gap(SiteLayout.Space.Large)
        }
        Breakpoint.MD {
            Modifier.gap(SiteLayout.Space.XXLarge)
        }
    }
}
